import React, { useState } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { http } from "middleware/apiMiddleware";
import Button from "components/Buttons/Button";
import InputField from "components/FormFields/InputField";
import toast from "react-hot-toast";
import { CardPurposeEnum } from "enum/common";

interface IEditCardFormProps {
  clientSecret: string;
  onCancel: () => void;
  onSuccess: () => void;
  businessUuid: string;
}

const EditCardForm: React.FC<IEditCardFormProps> = ({
  clientSecret,
  onCancel,
  onSuccess,
  businessUuid,
}) => {
  //state
  const [cardHolderName, setCardHoldername] = useState("");
  const [isUpdatingCard, setIsUpdatingCard] = useState(false);
  // hooks
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: any) => {
    try {
      event.preventDefault();
      if (!stripe || !elements || isUpdatingCard) {
        return;
      }
      setIsUpdatingCard(true);
      const cardElement = elements.getElement(CardNumberElement);
      //  Setup Intent
      if (cardElement) {
        const { setupIntent, error } = await stripe.confirmCardSetup(
          clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: cardHolderName.trim(),
              },
            },
          }
        );
        // send paymentMethodId to backend
        if (setupIntent && !error) {
          const { data, error } = await http.post(
            `/business/${businessUuid}/cards`,
            {
              paymentMethodId: setupIntent.payment_method,
              makeDefault: true,
              purpose: CardPurposeEnum.UPDATE_PAYMENT,
            }
          );
          if (data && !error) {
            onSuccess();
          } else {
            toast.error(error || "Error occured while updating card");
          }
        }
      }
    } catch (error) {
      toast.error("Error occured while updating card");
    } finally {
      setIsUpdatingCard(false);
    }
  };

  const options2 = {
    showIcon: true,
    style: {
      base: {
        maxWidth: "200px",
        padding: "20px",
        minHeight: 201,
        backgroundColor: "white",
        lineHeight: "40px",
        color: "#424770",
        letterSpacing: "0.025em",
      },
      invalid: {
        color: "#9e2146",
        border: "1px solid red",
      },
    },
  };

  const inputStyle = `rouded-md  border border-black20 rounded-md mt-2  px-4 font-poppins`;
  const labelText = `text-sm font-medium text-gray-900 text-opacity-80`;

  return (
    <form
      onSubmit={handleSubmit}
      className="h-full flex flex-col"
      id="update_card"
    >
      <div className="gap-2 flex-1">
        <div className="grid md:grid-cols-2 md:gap-x-4  gap-y-4">
          <InputField
            containerClass="!mb-0"
            customClass={`${inputStyle} !mb-[0px]`}
            placeholder="John Doe"
            onChange={function (e: any): void {
              setCardHoldername(e.target.value);
            }}
            value={cardHolderName}
            label={"Name on card"}
            id={""}
          />
          <div>
            <div className={labelText}>Card Number</div>
            <CardNumberElement className={inputStyle} options={options2} />
          </div>
          <div>
            <div className={labelText}>Expiry</div>
            <CardExpiryElement className={inputStyle} options={options2} />
          </div>
          <div>
            <div className={labelText}>CVC</div>
            <CardCvcElement className={inputStyle} options={options2} />
          </div>
        </div>
      </div>
      <div className="w-full justify-end md:items-end items-center md:flex-row flex-col-reverse flex gap-2">
        <Button
          label="Cancel"
          onClick={onCancel}
          variant="secondary"
          customClass="md:w-[158px] w-[95%] !me-0"
          disabled={isUpdatingCard}
        />
        <Button
          form="update_card"
          label="Update Card"
          type="submit"
          customClass="md:w-[150px] !text-md w-[95%] !me-0"
          disabled={isUpdatingCard}
          loading={isUpdatingCard}
        />
      </div>
    </form>
  );
};

export default EditCardForm;
